import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import Login, { AuthCallback } from "@/pages/Login";
import Home from "@/pages/learner/Home";
import MyLearning from "@/pages/learner/MyLearning";
import Discover from "@/pages/learner/Discover";
import CourseDetail from "@/pages/learner/CourseDetail";
import CoursePlayer from "@/pages/learner/CoursePlayer";
import Certificates, { Transcript, Verify } from "@/pages/learner/Certificates";
import Notifications from "@/pages/learner/Notifications";
import Team from "@/pages/manager/Team";
import Approvals from "@/pages/manager/Approvals";
import EmployeeDetail from "@/pages/manager/EmployeeDetail";
import Courses from "@/pages/contributor/Courses";
import CourseBuilder from "@/pages/contributor/CourseBuilder";
import Quizzes from "@/pages/contributor/Quizzes";
import Assignments from "@/pages/contributor/Assignments";
import BrandingCenter, { MediaLibrary } from "@/pages/branding/BrandingCenter";
import Manual from "@/pages/help/Manual";
import EmbedPlayer from "@/pages/embed/EmbedPlayer";
import EmailTemplates, { CertificateTemplates } from "@/pages/branding/EmailTemplates";
import AdminDashboard from "@/pages/admin/Dashboard";
import UsersPage from "@/pages/admin/Users";
import Campaigns from "@/pages/admin/Campaigns";
import Reports from "@/pages/admin/Reports";
import Integrations from "@/pages/admin/Integrations";
import Audit, { Settings } from "@/pages/admin/Audit";
import MigrationLayout from "@/pages/admin/migrations/MigrationLayout";
import MigrationOverview from "@/pages/admin/migrations/Overview";
import MigrationSources, { MigrationOAuthCallback } from "@/pages/admin/migrations/Sources";
import CourseBrowser from "@/pages/admin/migrations/CourseBrowser";
import MigrationWizard from "@/pages/admin/migrations/Wizard";
import MigrationQueue from "@/pages/admin/migrations/Queue";
import BatchDetail from "@/pages/admin/migrations/BatchDetail";
import UserMapping from "@/pages/admin/migrations/UserMapping";
import ReviewCenter from "@/pages/admin/migrations/ReviewCenter";
import MigrationSettings from "@/pages/admin/migrations/MigrationSettings";

function Guard({ perm, role, children }) {
  const { user, loading, has, hasRole } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  if (perm && !has(perm)) return <Navigate to="/" replace />;
  if (role && !hasRole(role)) return <Navigate to="/" replace />;
  return children;
}

/** Old bookmarks keep working after the navigation restructure. */
const LEGACY = {
  "/content/quizzes": "/content/banks",
  "/content/assignments": "/delivery/assignments",
  "/branding/media": "/content/media",
  "/branding/email-templates": "/org/branding/emails",
  "/branding/certificates": "/org/branding/certificates",
  "/branding": "/org/branding",
  "/admin/campaigns": "/delivery/campaigns",
  "/admin/reports": "/insights/reports",
  "/admin/audit": "/insights/audit",
  "/admin/users": "/org/people",
  "/admin/integrations": "/org/integrations",
  "/admin/settings": "/org/settings",
  "/admin": "/insights/dashboard",
};

function LegacyRedirect() {
  const loc = useLocation();
  const hit = Object.keys(LEGACY).sort((a, b) => b.length - a.length).find((k) => loc.pathname === k || loc.pathname.startsWith(`${k}/`));
  if (hit) return <Navigate to={loc.pathname.replace(hit, LEGACY[hit]) + loc.search} replace />;
  return <Navigate to="/" replace />;
}

function MigrationLegacy() {
  const loc = useLocation();
  return <Navigate to={loc.pathname.replace("/admin/migrations", "/org/migrations") + loc.search} replace />;
}

function CourseBuilderRoute() {
  const { id } = useParams();
  return <CourseBuilder key={id} />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify/:certId" element={<Verify />} />
          <Route path="/embed/player" element={<EmbedPlayer />} />
          <Route element={<Guard><AppShell /></Guard>}>
            {/* My Learning */}
            <Route path="/" element={<Home />} />
            <Route path="/my-learning" element={<MyLearning />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/courses/:id" element={<CourseDetail />} />
            <Route path="/learn/:enrollmentId" element={<CoursePlayer />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="/transcript" element={<Transcript />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/help" element={<Manual />} />
            {/* My Team */}
            <Route path="/team" element={<Guard perm="team.view"><Team /></Guard>} />
            <Route path="/team/approvals" element={<Guard perm="users.manage"><Approvals /></Guard>} />
            <Route path="/team/:userId" element={<EmployeeDetail />} />
            {/* Content */}
            <Route path="/content/courses" element={<Guard perm="courses.manage"><Courses /></Guard>} />
            <Route path="/content/courses/:id" element={<Guard perm="courses.manage"><CourseBuilderRoute /></Guard>} />
            <Route path="/content/banks" element={<Guard perm="quizzes.manage"><Quizzes /></Guard>} />
            <Route path="/content/media" element={<Guard perm="media.upload"><MediaLibrary /></Guard>} />
            {/* Delivery */}
            <Route path="/delivery/assignments" element={<Guard perm="assignments.manage"><Assignments /></Guard>} />
            <Route path="/delivery/audiences" element={<Guard perm="assignments.manage"><Assignments /></Guard>} />
            <Route path="/delivery/campaigns" element={<Guard perm="campaigns.manage"><Campaigns /></Guard>} />
            {/* Insights */}
            <Route path="/insights/dashboard" element={<Guard perm="reports.all"><AdminDashboard /></Guard>} />
            <Route path="/insights/reports" element={<Reports />} />
            <Route path="/insights/audit" element={<Guard perm="audit.view"><Audit /></Guard>} />
            {/* Organisation */}
            <Route path="/org/people" element={<Guard perm="users.manage"><UsersPage /></Guard>} />
            <Route path="/org/branding" element={<Guard perm="branding.manage"><BrandingCenter /></Guard>} />
            <Route path="/org/branding/emails" element={<Guard perm="email_templates.manage"><EmailTemplates /></Guard>} />
            <Route path="/org/branding/certificates" element={<Guard perm="certificate_templates.manage"><CertificateTemplates /></Guard>} />
            <Route path="/org/integrations" element={<Guard perm="integrations.manage"><Integrations /></Guard>} />
            <Route path="/org/settings" element={<Guard perm="settings.manage"><Settings /></Guard>} />
            <Route path="/org/migrations" element={<Guard perm="migrations.manage"><MigrationLayout /></Guard>}>
              <Route index element={<MigrationOverview />} />
              <Route path="sources" element={<MigrationSources />} />
              <Route path="courses" element={<CourseBrowser />} />
              <Route path="new" element={<MigrationWizard />} />
              <Route path="queue" element={<MigrationQueue />} />
              <Route path="queue/:batchId" element={<BatchDetail />} />
              <Route path="users" element={<UserMapping />} />
              <Route path="review" element={<ReviewCenter />} />
              <Route path="settings" element={<MigrationSettings />} />
              <Route path="oauth/callback" element={<MigrationOAuthCallback />} />
            </Route>
            <Route path="/admin/migrations/*" element={<MigrationLegacy />} />
            <Route path="*" element={<LegacyRedirect />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
